import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { IconShield } from '@tabler/icons-react';
import { functions } from '../firebase';
import { useAdminView } from '../contexts/AdminViewContext';
import { haversineM, positionAt, prepareRoute, storyTimeline } from './adminVideosStreetView';
import './AdminVideosPage.css';

// Background highlight videos of real activities (functions/src/video_pipeline.ts): admins only, never shown or
// uploaded to the athletes. Each activity's video with how long each stage took, what it cost and what made it.
const getVideoPipeline = httpsCallable(functions, 'getVideoPipelineForAdmin');
const setVideoPipelineConfig = httpsCallable(functions, 'setVideoPipelineConfig');

const STATE_LABEL = { world: 'Building world', rendering: 'Rendering', story: 'Making the story', done: 'Done', failed: 'Failed', skipped: 'Skipped' };
const FILTERS = [['all', 'All'], ['done', 'Done'], ['active', 'In progress'], ['failed', 'Failed'], ['skipped', 'Skipped']];
const KIND_LABEL = { tool_damage: 'undoing tool damage', data_error: 'fixing data errors', both: 'both' };
const pct = (n, d) => (d ? `${Math.round((100 * n) / d)}%` : '–');
const money = (v) => (typeof v === 'number' ? `$${v.toFixed(2)}` : '–');
const cents = (v) => (typeof v === 'number' ? `$${v.toFixed(v < 0.1 ? 3 : 2)}` : '–'); // (story videos cost about a cent)
const mins = (v) => (typeof v === 'number' ? `${v.toFixed(1)} min` : '–');
const when = (ms) => (ms ? new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const minsBetween = (a, b) => (a && b && b >= a ? (b - a) / 60000 : null);
// RunPod serverless flex rate by GPU memory: the same rule as RATE_PER_H in functions/src/video_pipeline.ts
const gpuRatePerH = (gpu) => (/A5000|L4|3090|A10/.test(gpu || '') ? 0.69 : 0.58);
const ACTIVE = ['world', 'rendering', 'story'];
const NO_VIDEO = ['skipped', 'failed'];
const PAGE_SIZE = 10;

// One video's figures, for flyover jobs (timings and cost saved by the pipeline) and story-only jobs (worked out
// from the story's own fields: the pipeline marks those done when the story is queued, before it renders)
function figures(item) {
  const s = item.story || {};
  const storyMin = typeof s.executionMs === 'number' ? s.executionMs / 60000 : null;
  const storyUsd = storyMin !== null ? (storyMin / 60) * gpuRatePerH(s.gpu) : null;
  if (item.storyOnly) {
    const state = item.state !== 'done' ? item.state : s.state === 'done' ? 'done' : s.state === 'failed' ? 'failed' : 'story';
    return {
      state,
      // (a re-rendered story keeps its old world: no world or GPU-wait time; start to finish runs from the re-render's
      // queueing, its createdAt, except on the first test requeue that kept the old createdAt)
      worldMin: item.requeuedAt || item.requeuedNote ? null : minsBetween(item.createdAt, (item.world || {}).readyAt),
      gpuWaitMin: item.requeuedAt || item.requeuedNote ? null : minsBetween(item.finishedAt, s.submittedAt),
      renderMin: storyMin,
      endToEndMin: s.state === 'done' && (item.requeuedAt || !item.requeuedNote) ? minsBetween(item.createdAt, s.updatedAt) : null,
      gpuUsd: storyUsd,
      totalUsd: storyUsd !== null || facadeUsd(item) ? (storyUsd || 0) + facadeUsd(item) : null,
    };
  }
  const t = item.timings || {};
  const c = item.cost || {};
  const extra = (s.state === 'done' && storyUsd !== null ? storyUsd : 0) + facadeUsd(item); // (a story made after the flyover, and the facade stage)
  return {
    state: item.state,
    worldMin: typeof t.worldWaitMin === 'number' ? t.worldWaitMin : null,
    renderMin: typeof t.jobMin === 'number' ? t.jobMin + (extra ? storyMin : 0) : null,
    endToEndMin: item.state === 'done' ? minsBetween(item.createdAt, item.finishedAt) : null,
    gpuUsd: typeof c.gpuUsd === 'number' ? c.gpuUsd + extra : null,
    totalUsd: typeof c.totalUsd === 'number' ? c.totalUsd + extra : null,
  };
}
const average = (list) => (list.length ? list.reduce((a, b) => a + b, 0) / list.length : null);

// The summary, one row per version of each kind of video (a flyover's job and a story's render are different work):
// flyovers by pipeline version, stories by the story renderer that made them (story-v1, unity-v2, unity-v4: the
// pipeline version stays the same across them); averages over each row's done videos that have the figure
const KIND_NAME = { story: 'Story only', flyover: 'Flyover' };
function byVersion(items) {
  const rows = new Map();
  items.forEach((i) => {
    const pipeline = (i.version && i.version.pipeline) || i.pipelineVersion || 'unknown';
    const kind = i.storyOnly ? 'story' : 'flyover';
    const version = kind === 'story' ? (i.storyRenderer || 'renderer unknown') : pipeline;
    const key = `${kind}|${version}`;
    if (!rows.has(key)) rows.set(key, { key, version, pipeline, kind, all: [], first: Infinity, last: 0, lastDone: 0 });
    const r = rows.get(key);
    r.all.push(i);
    // (when the video was made: a story's render, else the job's finish; a story re-rendered later counts then)
    const at = (kind === 'story' ? (i.story || {}).updatedAt : i.finishedAt) || i.createdAt;
    if (at) { r.first = Math.min(r.first, at); r.last = Math.max(r.last, at); }
    if (i.fg.state === 'done' && at) r.lastDone = Math.max(r.lastDone, at);
  });
  return [...rows.values()].map((r) => {
    const done = r.all.filter((i) => i.fg.state === 'done');
    const avgOf = (k) => average(done.map((i) => i.fg[k]).filter((x) => typeof x === 'number'));
    return {
      ...r,
      done: done.length,
      failed: r.all.filter((i) => i.fg.state === 'failed').length,
      active: r.all.filter((i) => ACTIVE.includes(i.fg.state)).length,
      renderMin: avgOf('renderMin'),
      worldMin: avgOf('worldMin'),
      endToEndMin: avgOf('endToEndMin'),
      avgUsd: avgOf('totalUsd'),
      totalUsd: done.reduce((s, i) => s + (i.fg.totalUsd || 0), 0),
    };
  }).sort((a, b) => b.lastDone - a.lastDone); // (the row with the newest video first)
}
const day = (ms) => (ms && Number.isFinite(ms) ? new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');

// Signed links change on every load: keep the link a card already has (same file, still fresh) so a reload
// doesn't swap the player's source and stop a video that is playing
const LINK_KEEP_MS = 2 * 3600e3; // (links are signed for 3 h)
function keepLinks(items, cache) {
  const now = Date.now();
  return items.map((item) => {
    if (!item.urls) return item;
    const urls = {};
    Object.entries(item.urls).forEach(([k, u]) => {
      const file = u.split('?')[0];
      const kept = cache.get(file);
      if (kept && now - kept.at < LINK_KEEP_MS) urls[k] = kept.url;
      else { cache.set(file, { url: u, at: now }); urls[k] = u; }
    });
    return { ...item, urls };
  });
}

const secs = (v) => (typeof v !== 'number' ? '–' : v < 90 ? `${Math.round(v)} s` : `${(v / 60).toFixed(1)} min`);
const eta = (v) => {
  if (typeof v !== 'number' || v < 0) return null;
  if (v < 60) return 'under a minute left';
  const m = Math.round(v / 60);
  return m < 60 ? `about ${m} min left` : `about ${Math.floor(m / 60)} h ${m % 60} min left`;
};
// (a row's cost: the job's own total plus its facade stage's GPU time)
const facadeUsd = (i) => (i.facades && typeof i.facades.costUsd === 'number' ? i.facades.costUsd : 0);
const itemUsd = (i) => ((i.cost || {}).totalUsd || 0) + facadeUsd(i);

// The facade stage (Street View photos -> building recipes on our own GPU, before the story video). States carry a
// symbol and words, never colour alone.
const FACADE_ACTIVE = ['queued', 'prep', 'infer', 'saving'];
const FACADE_STATE = {
  plan: ['◷', 'Waiting for the story\'s camera plan', 'rendering'], queued: ['◷', 'Queued', 'rendering'], prep: ['▶', 'Preparing photos', 'rendering'], infer: ['▶', 'Making recipes', 'rendering'],
  saving: ['▶', 'Saving recipes', 'rendering'], done: ['✓', 'Done', 'done'], failed: ['✕', 'Failed', 'failed'], skipped: ['–', 'Skipped', 'skipped'],
};
const FACADE_STAGE = {
  pick: 'picking buildings', harvest: 'fetching Street View', refine: 'refining photos', rectify: 'rectifying walls',
  facts: 'reading map facts', greenery: 'finding greenery', infer: 'making recipes', save: 'saving recipes',
};
const FACADE_COUNTS = [['route', 'on the route'], ['focus', 'near the close shots'], ['cached', 'made before'], ['todo', 'to do'], ['photographed', 'photographed'], ['made', 'made'], ['noPhoto', 'no photo'], ['invalid', 'invalid']];

// What made a video: the renderer (with its build), the pipeline version and where its buildings came from
const RECIPE_MODEL = { 'recipe-v3-osm-colour@1987': 'Recipe v3, Fukuoka student' };
function MadeWith({ item }) {
  const s = item.story || {};
  const v = item.version || {};
  const renderer = item.storyOnly ? (s.overlay || item.storyRenderer || null) : (v.image ? v.image.split(':').pop() : null);
  const [rName, ...rBuild] = String(renderer || 'unknown').split(' ');
  const r = s.recipes;
  const fc = item.facades;
  let buildings = 'rules (no recipes)';
  if (r && r.matched) buildings = `${(r.models || []).map((m) => RECIPE_MODEL[m] || m).join(', ') || 'Recipe v3'}: ${r.matched} buildings`;
  else if (fc && (FACADE_ACTIVE.includes(fc.state) || fc.state === 'plan')) buildings = 'rules for now, Recipe v3 next';
  else if (!item.storyOnly || (s.state && s.state !== 'done')) buildings = null;
  return (
    <dl className="vp-made" aria-label="Made with">
      <div><dt>Renderer</dt><dd><b>{rName}</b>{rBuild[0] ? <span className="mono"> {rBuild[0]}</span> : null}</dd></div>
      <div><dt>Pipeline</dt><dd><b className="mono">{v.pipeline || item.pipelineVersion || '?'}</b></dd></div>
      {buildings && <div><dt>Buildings</dt><dd>{buildings}</dd></div>}
    </dl>
  );
}

function FacadeStage({ fc }) {
  const [icon, label, tone] = FACADE_STATE[fc.state] || ['?', fc.state || 'unknown', 'skipped'];
  const active = FACADE_ACTIVE.includes(fc.state);
  const b = fc.buildings || {};
  const tm = fc.timings || {};
  const net = fc.network || {};
  const hosts = Array.isArray(net.hosts) ? net.hosts : [];
  const progress = Math.max(0, Math.min(1, Number(fc.progress) || 0));
  const left = active ? eta(fc.etaS) : null;
  return (
    <section className="vp-facades" aria-label="Facade stage">
      <div className="vp-fc-head">
        <b>Facades</b>
        <span className={`vp-pill vp-pill-${tone}`}><span aria-hidden="true">{icon}</span> {label}</span>
        {active && fc.stage && <span className="vp-fc-stage">{FACADE_STAGE[fc.stage] || fc.stage}</span>}
        {net.aiApi === false && <span className="vp-fc-net" title={hosts.length ? `Hosts contacted: ${hosts.join(', ')}` : undefined}>No external AI API</span>}
        {net.aiApi === true && <span className="vp-fc-net vp-fc-net-warn" title={hosts.join(', ')}><span aria-hidden="true">⚠</span> External AI API contacted</span>}
      </div>
      {(active || fc.state === 'done') && (
        <div className="vp-fc-progress">
          <div className={`vp-bar${active ? ' vp-bar-active' : ''}`} role="progressbar" aria-label="Facade progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
            <span style={{ width: `${(fc.state === 'done' ? 1 : progress) * 100}%` }} />
          </div>
          <span className="vp-fc-numbers">{fc.state === 'done' ? '100%' : `${Math.round(progress * 100)}%`}{left ? ` · ${left}` : ''} · {money(fc.costUsd)}</span>
        </div>
      )}
      {Object.keys(b).length > 0 && (
        <ul className="vp-fc-counts" aria-label="Buildings">
          {FACADE_COUNTS.filter(([k]) => typeof b[k] === 'number').map(([k, l]) => <li key={k}><b>{b[k]}</b> {l}</li>)}
        </ul>
      )}
      {fc.state === 'done' && (
        <p className="vp-fc-line">Prep {secs(tm.prepS)} · inference {secs(tm.inferS)}{tm.queueS ? ` · queue ${secs(tm.queueS)}` : ''} · total {secs(tm.totalS)}</p>
      )}
      {fc.state === 'failed' && (
        <p className="vp-fc-line vp-error"><span aria-hidden="true">✕ </span>{fc.error || 'Failed without a message'}{typeof fc.costUsd === 'number' ? ` · ${money(fc.costUsd)} spent` : ''} · the story still renders, drawn by rules</p>
      )}
      {hosts.length > 0 && (
        <details className="vp-fc-hosts"><summary>Hosts the workers contacted ({hosts.length})</summary><p>{hosts.join(', ')}</p></details>
      )}
    </section>
  );
}

// Street View at the story video's spot: summary + route fetched once per file (signed URLs change on every list
// load, so the cache is keyed by the path without its query).
const EMBED_KEY = process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY;
const baseUrl = (u) => String(u || '').split('?')[0];
const jsonCache = new Map();
const fetchJson = (u) => {
  const k = baseUrl(u);
  if (!jsonCache.has(k)) {
    jsonCache.set(k, fetch(u).then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .catch((err) => { jsonCache.delete(k); throw err; }));
  }
  return jsonCache.get(k);
};
const MOVE_M = 15;
const PLAY_EVERY_MS = 2000;
const km = (m) => `${(m / 1000).toFixed(2)} km`;
const kindLabel = (k) => (k === 'hold' ? 'hold (zoom-in)' : k);

function StreetViewPanel({ summaryUrl, routeUrl, video }) {
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [geo, setGeo] = useState(null);
  const [failed, setFailed] = useState(null);
  const [view, setView] = useState(null);
  const [now, setNow] = useState(null);
  const [follow, setFollow] = useState(true);
  const viewRef = useRef(null);
  const lastRef = useRef(0);

  // (lazy: only once the panel is near the screen)
  useEffect(() => {
    const el = rootRef.current;
    if (!el || visible) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) setVisible(true); }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const summaryKey = baseUrl(summaryUrl);
  const routeKey = baseUrl(routeUrl);
  useEffect(() => {
    if (!visible) return undefined;
    let live = true;
    Promise.all([fetchJson(summaryUrl), fetchJson(routeUrl)]).then(([summary, points]) => {
      if (!live) return;
      const route = prepareRoute(points);
      const timeline = storyTimeline(summary, route.length);
      if (!route.pts.length || !timeline.segs.length) { setFailed('no route or timeline in the files'); return; }
      setGeo({ route, timeline });
    }).catch((err) => { if (live) setFailed(err.message || 'could not load'); });
    return () => { live = false; };
    // (the keys, not the signed URLs: a list refresh must not refetch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, summaryKey, routeKey]);

  const place = useCallback((force) => {
    if (!geo) return;
    const p = positionAt(geo.timeline, geo.route, video ? video.currentTime || 0 : 0);
    if (!p) return;
    setNow(p);
    if (!force && !follow) return;
    const cur = viewRef.current;
    if (cur && !force && haversineM([cur.lat, cur.lon], [p.lat, p.lon]) <= MOVE_M) return;
    viewRef.current = p;
    lastRef.current = Date.now();
    setView(p);
  }, [geo, video, follow]);

  // (the first spot as soon as the files are in; following again jumps to the video's spot)
  useEffect(() => { if (geo && (!viewRef.current || follow)) place(!viewRef.current); }, [geo, follow, place]);

  useEffect(() => {
    if (!video || !geo) return undefined;
    const onStop = () => place(false);
    const onTime = () => {
      if (video.paused) return;
      if (Date.now() - lastRef.current >= PLAY_EVERY_MS) { lastRef.current = Date.now(); place(false); }
    };
    video.addEventListener('pause', onStop);
    video.addEventListener('seeked', onStop);
    video.addEventListener('timeupdate', onTime);
    return () => {
      video.removeEventListener('pause', onStop);
      video.removeEventListener('seeked', onStop);
      video.removeEventListener('timeupdate', onTime);
    };
  }, [video, geo, place]);

  const ll = view ? `${view.lat.toFixed(6)},${view.lon.toFixed(6)}` : '';
  const h = view ? Math.round(view.heading) : 0;
  const total = geo ? geo.route.length : 0;
  return (
    <div className="vp-sv" ref={rootRef}>
      {view && EMBED_KEY ? (
        <iframe
          title="Street View at the video's spot"
          src={`https://www.google.com/maps/embed/v1/streetview?key=${EMBED_KEY}&location=${ll}&heading=${h}&pitch=0&fov=80`}
          loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div className="vp-sv-empty">
          {failed ? `Street View: ${failed}` : !view ? 'Street View loads when the video is on screen…' : 'No embed key (REACT_APP_GOOGLE_MAPS_EMBED_KEY): use the link below.'}
        </div>
      )}
      {view && (
        <div className="vp-sv-info">
          <p>Street View at <b>{km(view.routeS)}</b> of {km(total)} · {kindLabel(view.kind)} · heading {h}°</p>
          {!follow && now && Math.abs(now.routeS - view.routeS) > MOVE_M && <p className="vp-sv-now">Video now at {km(now.routeS)} · {kindLabel(now.kind)}</p>}
          <div className="vp-sv-actions">
            <label className="vp-sv-follow"><input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> Follow video</label>
            <a href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${ll}&heading=${h}`} target="_blank" rel="noreferrer">Open in Google Maps ↗</a>
          </div>
        </div>
      )}
    </div>
  );
}

function VideoCard({ item }) {
  const [which, setWhich] = useState(item.hasVideo2 ? 'video2' : item.urls && !item.urls.video1 && item.urls.story ? 'story' : 'video1');
  const a = item.activity || {};
  const t = item.timings || {};
  const c = item.cost || {};
  const ap = item.aiPass || {};
  const f = ap.faults || {};
  const v = item.version || {};
  const fg = figures(item);
  const s = item.story || {};
  const [videoEl, setVideoEl] = useState(null);
  // (the chosen video, or the first one there is: a story can arrive after the card first showed)
  const urls = item.urls || {};
  const shownVideo = urls[which] ? which : ['video2', 'story', 'video1'].find((k) => urls[k]);
  const src = shownVideo ? urls[shownVideo] : null;
  const sv = shownVideo === 'story' && urls.storySummary && urls.route;
  return (
    <article className={`vp-card vp-${fg.state}${src && sv ? ' vp-card-sv' : ''}`}>
      {src ? (
        <div className="vp-player">
          <div className={sv ? 'vp-media vp-media-sv' : 'vp-media'}>
            <video key={src} ref={setVideoEl} src={src} controls muted playsInline preload="metadata" />
            {sv && <StreetViewPanel summaryUrl={urls.storySummary} routeUrl={urls.route} video={videoEl} />}
          </div>
          {Object.keys(urls).filter((k) => !['storySummary', 'route'].includes(k)).length > 1 && (
            <div className="vp-switch" role="group" aria-label="Which video">
              {urls.video1 && <button type="button" className={shownVideo === 'video1' ? 'on' : ''} onClick={() => setWhich('video1')}>After the tools</button>}
              {item.hasVideo2 && <button type="button" className={shownVideo === 'video2' ? 'on' : ''} onClick={() => setWhich('video2')}>After Claude</button>}
              {item.urls && item.urls.story && <button type="button" className={shownVideo === 'story' ? 'on' : ''} onClick={() => setWhich('story')}>Story</button>}
            </div>
          )}
          {shownVideo === 'story' && item.story && (item.story.series || item.story.hook) && (
            <p className="vp-story">{[item.story.series, item.story.hook].filter(Boolean).join(' · ')}{item.story.fallback ? ' (rendered as the flyover)' : ''}</p>
          )}
        </div>
      ) : (
        <div className="vp-placeholder">
          <span className={`vp-pill vp-pill-${fg.state}`}>{STATE_LABEL[fg.state] || fg.state}</span>
          {fg.state === 'story' && <p>{s.state === 'rendering' ? `Rendering on ${s.gpu || 'RunPod'}` : 'Waiting for a GPU'}</p>}
          {item.storyOnly && fg.state === 'failed' && <p className="vp-error">{s.error}</p>}
          {item.state === 'world' && item.world && (item.waitingFor
            ? <p>World ready · waiting for {item.waitingFor}</p>
            : <p>World tiles on the route ready: {item.world.ready ?? 0} of {item.world.corridor ?? '?'}</p>)}
          {item.state === 'rendering' && item.job && <p>{item.job.stage || item.job.status} · {Math.round((item.job.progress || 0) * 100)}%</p>}
          {item.state === 'failed' && <p className="vp-error">{item.error}</p>}
        </div>
      )}
      <div className="vp-info">
        <div className="vp-title">
          <h3>{a.name || item.id}</h3>
          <span className={`vp-pill vp-pill-${fg.state}`}>{STATE_LABEL[fg.state] || fg.state}</span>
        </div>
        <p className="vp-meta">{a.type} · {a.km} km · {a.elevationM} m · {a.place || 'unknown place'} · {item.requeuedAt ? 're-rendered' : 'queued'} {when(item.createdAt)}</p>
        <MadeWith item={item} />
        {item.highlights && item.highlights.length > 0 && (
          <ul className="vp-chips">
            {item.highlights.map((h, i) => <li key={i}><b>{h.label}</b> {h.value}{h.unit ? ` ${h.unit}` : ''}</li>)}
          </ul>
        )}
        {item.storyOnly && fg.state === 'done' && (
          <dl className="vp-grid">
            <dt>World build</dt><dd>{mins(fg.worldMin)}</dd>
            <dt>Waiting for a GPU</dt><dd>{mins(fg.gpuWaitMin)}</dd>
            <dt>Story render</dt><dd>{mins(fg.renderMin)}</dd>
            <dt>Start to finish</dt><dd className="strong">{mins(fg.endToEndMin)}</dd>
            <dt>GPU</dt><dd>{money(fg.gpuUsd)}{s.gpu ? ` · ${s.gpu}` : ''}</dd>
            {item.facades && <><dt>Facades</dt><dd>{money(item.facades.costUsd)}</dd></>}
            <dt>Total cost</dt><dd className="strong">{money(fg.totalUsd)}</dd>
            <dt>Video</dt><dd>{typeof s.duration === 'number' ? `${Math.round(s.duration)} s` : '–'}{s.weather && s.weather.label ? ` · ${s.weather.label}` : ''}{s.ghost ? ' · raced an earlier ride' : ''}</dd>
          </dl>
        )}
        {!item.storyOnly && item.state === 'done' && (
          <dl className="vp-grid">
            <dt>World build</dt><dd>{mins(t.worldWaitMin)}</dd>
            {t.slotWaitMin > 0 && <><dt>Waiting to render</dt><dd>{mins(t.slotWaitMin)}</dd></>}
            <dt>Tools</dt><dd>{mins(t.toolsMin)}</dd>
            <dt>Video 1</dt><dd>{mins(t.video1Min)}</dd>
            <dt>Claude</dt><dd>{ap.claude && ap.claude.ran ? mins(t.claudeMin) : 'not needed'}</dd>
            <dt>Video 2</dt><dd>{item.hasVideo2 ? mins(t.video2Min) : '–'}</dd>
            <dt>Job total</dt><dd className="strong">{mins(t.jobMin)}</dd>
            <dt>GPU</dt><dd>{money(c.gpuUsd)} {item.job && item.job.gpu ? `· ${item.job.gpu}` : ''}</dd>
            <dt>Claude</dt><dd>{money(c.claudeUsd)}</dd>
            {item.facades && <><dt>Facades</dt><dd>{money(item.facades.costUsd)}</dd></>}
            <dt>Total cost</dt><dd className="strong">{money(item.facades ? itemUsd(item) : c.totalUsd)}</dd>
            <dt>Course faults</dt><dd>{f.before ?? '?'} → {f.tools ?? '?'} after tools{item.hasVideo2 ? ` → ${f.after ?? '?'} after Claude` : ''}</dd>
            <dt>Tiles</dt><dd>{ap.tiles ?? 0} processed, {ap.saved ?? 0} corrected, {ap.skipped ?? 0} done before</dd>
            {ap.smooth && ap.smooth.toolsDefects !== null && (
              <><dt>Bumps and pits</dt><dd>{ap.smooth.toolsDefects} made by the tools{item.hasVideo2 ? `, ${ap.smooth.claudeFixed ?? 0} fixed by Claude, ${ap.smooth.afterDefects ?? 0} left` : ''}</dd></>
            )}
            {ap.kinds && Object.keys(ap.kinds).length > 0 && (
              <><dt>Claude's edits</dt><dd>{Object.entries(ap.kinds).map(([k, n]) => `${n} ${KIND_LABEL[k] || k}`).join(', ')}{ap.sample ? ' (sampled run)' : ''}</dd></>
            )}
            <dt>Highlights data</dt><dd>{item.streams ? (item.streams.startsWith('Strava') || item.streams.startsWith('no ') ? item.streams : 'Strava streams') : '–'}</dd>
          </dl>
        )}
        {item.facades && <FacadeStage fc={item.facades} />}
        {ap.summary && <details className="vp-claude"><summary>Claude's notes</summary><p>{ap.summary}</p></details>}
        <p className="vp-version">
          {item.storyOnly ? (item.storyRenderer || 'renderer ?') : v.image ? v.image.split(':').pop() : 'image ?'}{v.worker ? ` · ${v.worker}` : ''} · pipeline {v.pipeline || item.pipelineVersion}
          {' · '}<a href={`https://www.strava.com/activities/${item.stravaId}`} target="_blank" rel="noreferrer">Strava activity</a>
          {' · '}generation {item.generationId}
        </p>
      </div>
    </article>
  );
}

function AdminVideosPage({ user }) {
  const { isAdmin } = useAdminView();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [cap, setCap] = useState('');
  const [saving, setSaving] = useState(false);
  const [hideNoVideo, setHideNoVideo] = useState(() => {
    try { return localStorage.getItem('vp-hide-no-video') !== '0'; } catch { return true; }
  });
  // pages[i] is the cursor (a createdAt in ms) page i starts after; null for the newest page
  const [pages, setPages] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [recent, setRecent] = useState(null); // (the summary's records: loaded with the first page)
  const links = useRef(new Map());
  const listTop = useRef(null);

  const cursor = pages[pageIndex];
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVideoPipeline({ pageSize: PAGE_SIZE, before: cursor, filter, hideNoVideo, summary: pageIndex === 0 });
      setData({ ...res.data, items: keepLinks(res.data.items || [], links.current) });
      if (res.data.recent) setRecent(res.data.recent);
      setPages((p) => (res.data.next && p.length === pageIndex + 1 ? [...p, res.data.next] : p));
      setCap(String(res.data.config.dailyCap));
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not load the videos.');
    }
    setLoading(false);
  }, [cursor, filter, hideNoVideo, pageIndex]);

  const facadesRunning = !!data && (data.items || []).some((i) => i.facades && FACADE_ACTIVE.includes(i.facades.state));
  useEffect(() => {
    if (user && isAdmin) load();
  }, [user, isAdmin, load]);
  // (every minute; every 30 s while some facade stage is running)
  useEffect(() => {
    if (!user || !isAdmin) return undefined;
    const t = setInterval(load, facadesRunning ? 30000 : 60000);
    return () => clearInterval(t);
  }, [user, isAdmin, load, facadesRunning]);

  // a new tab or toggle starts again from the newest page
  const restart = () => { setPages([null]); setPageIndex(0); };
  const chooseFilter = (k) => { if (k !== filter) { setFilter(k); restart(); } };
  const toggleHide = (on) => {
    setHideNoVideo(on); restart();
    try { localStorage.setItem('vp-hide-no-video', on ? '1' : '0'); } catch { /* (not remembered) */ }
  };
  const goTo = (i) => {
    setPageIndex(i);
    if (listTop.current) listTop.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const hasOlder = !!(data && data.next) && pages.length > pageIndex + 1;

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      const res = await setVideoPipelineConfig(patch);
      setData((d) => ({ ...d, config: res.data.config }));
    } catch (err) {
      setError(err.message || 'Could not save the settings.');
    }
    setSaving(false);
  };

  // (the list is this page, filtered by the function; the summary and tab counts are every record past the cap,
  // with the skipped ones only counted)
  const shown = data ? data.items : [];
  const items = useMemo(() => (recent || []).map((i) => ({ ...i, fg: figures(i) })), [recent]);
  const versions = useMemo(() => byVersion(items), [items]);
  const inFilter = (i, k) => (!hideNoVideo || !NO_VIDEO.includes(i.fg.state) || k === i.fg.state)
    && (k === 'all' || (k === 'active' ? ACTIVE.includes(i.fg.state) : i.fg.state === k));
  const skipped = (data && data.skippedCount) || 0;
  const counts = Object.fromEntries(FILTERS.map(([k]) => [k, items.filter((i) => inFilter(i, k)).length
    + ((k === 'skipped' || (k === 'all' && !hideNoVideo)) ? skipped : 0)]));

  if (!user || !isAdmin) {
    return (
      <div className="admin-videos-page">
        <div className="vp-denied"><IconShield size={48} /><h2>Admin access required</h2></div>
      </div>
    );
  }

  const cfg = data && data.config;
  const today = data && data.days && data.days[0];
  return (
    <div className="admin-videos-page">
      <header className="vp-header">
        <Link to="/admin-menu" className="vp-back">← Admin</Link>
        <h1>Background videos</h1>
        <p>Highlight videos made in the background for real activities after their map is generated. Only admins see them: nothing is shown or uploaded to the athletes.</p>
      </header>

      {error && <div className="vp-banner">{error}</div>}

      {cfg && (
        <section className="vp-controls" aria-label="Settings">
          <label className="vp-toggle">
            <input type="checkbox" checked={!!cfg.enabled} disabled={saving} onChange={(e) => saveConfig({ enabled: e.target.checked })} />
            <span>{cfg.enabled ? 'On: new activities are queued' : 'Off: nothing new is queued'}</span>
          </label>
          <form className="vp-cap" onSubmit={(e) => { e.preventDefault(); saveConfig({ dailyCap: parseInt(cap, 10) }); }}>
            <label htmlFor="vp-cap-input">Videos a day</label>
            <input id="vp-cap-input" type="number" min="0" max="500" value={cap} onChange={(e) => setCap(e.target.value)} />
            <button type="submit" disabled={saving || String(cfg.dailyCap) === cap}>Save</button>
          </form>
          <label className="vp-claude-mode" htmlFor="vp-claude-select">Claude review
            <select id="vp-claude-select" value={cfg.claude} disabled={saving} onChange={(e) => saveConfig({ claude: e.target.value })}>
              <option value="auto">When faults are left</option>
              <option value="never">Never</option>
              <option value="always">Always</option>
            </select>
          </label>
          <p className="vp-today">Today (UTC): {today && today.day === new Date().toISOString().slice(0, 10) ? `${today.accepted || 0} queued, ${today.skipped || 0} over the cap` : 'none yet'}</p>
        </section>
      )}

      <section className="vp-versions" aria-label="Summary by pipeline version">
        <h2>By pipeline version</h2>
        <div className="vp-versions-scroll">
          <table>
            <thead>
              <tr>
                <th>Kind</th><th>Version</th><th>Dates</th><th>Videos</th><th>Failed</th>
                <th>Avg render</th><th>Avg world build</th><th>Avg start to finish</th><th>Avg cost</th><th>Total cost</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((r) => (
                <tr key={r.key} className={r === versions[0] ? 'current' : ''}>
                  <td>{KIND_NAME[r.kind]}</td>
                  <td className="mono">{r.version}{r.kind === 'story' ? ` · pipeline ${r.pipeline}` : ''}{r === versions[0] ? ' (now)' : ''}</td>
                  <td>{day(r.first)}{day(r.last) !== day(r.first) ? ` to ${day(r.last)}` : ''}</td>
                  <td>{r.done}{r.active ? ` (+${r.active} in progress)` : ''}</td>
                  <td>{r.failed ? `${r.failed} (${pct(r.failed, r.done + r.failed)})` : '0'}</td>
                  <td>{mins(r.renderMin)}</td>
                  <td>{mins(r.worldMin)}</td>
                  <td>{mins(r.endToEndMin)}</td>
                  <td>{cents(r.avgUsd)}</td>
                  <td>{money(r.totalUsd)}</td>
                </tr>
              ))}
              {versions.length > 1 && (
                <tr className="total">
                  <td colSpan={3}>All versions</td>
                  <td>{versions.reduce((n, r) => n + r.done, 0)}</td>
                  <td>{versions.reduce((n, r) => n + r.failed, 0)}</td>
                  <td colSpan={4} />
                  <td>{money(versions.reduce((n, r) => n + r.totalUsd, 0))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="vp-stats-note">A flyover's render is the whole job (tools, video, Claude when it ran); a story's is its one render. {skipped ? `${skipped} activities were skipped over the daily cap and are not in these figures.` : ''}</p>
      </section>

      {data && data.scoreboard && (() => {
        const sb = data.scoreboard; const v = sb.votes || {}; const n = sb.reviewed || 0;
        return (
          <section className="vp-score" aria-label="Is Claude's review worth it">
            <h2>Is Claude's review worth it?</h2>
            <div className="vp-score-grid">
              <div><span>Claude reviewed</span><b>{sb.claudeRuns} videos: {sb.claudeEdited} edited, {sb.claudeNoEdit} unchanged</b></div>
              <div><span>Extra cost</span><b>{money(sb.extraUsd)} total · {money(sb.extraPerRunUsd)} a review</b></div>
              <div><span>Blind votes ({n})</span><b>Claude better {pct(v.claude || 0, n)} · same {pct(v.same || 0, n)} · tools better {pct(v.tools || 0, n)} · both bad {pct(v.both_bad || 0, n)}</b></div>
              <div><span>Cost per visible improvement</span><b>{sb.costPerWinUsd !== null ? money(sb.costPerWinUsd) : 'no votes for Claude yet'}</b></div>
              <div><span>What Claude fixed</span><b>{Object.keys(sb.kinds || {}).length ? Object.entries(sb.kinds).map(([k, c]) => `${c} ${KIND_LABEL[k] || k}`).join(' · ') : 'no tagged edits yet'}</b></div>
              <div><span>Tools' own damage</span><b>{sb.tools.withDefects} of {sb.tools.videos} videos ({sb.tools.defects} bumps or pits): Claude fixed {sb.tools.fixedByClaude}, {sb.tools.leftWhenClaudeSkipped} videos had some with no Claude review</b></div>
              <div><span>Sampled "always" reviews</span><b>{sb.sample.runs} runs: {sb.sample.wouldHaveSkipped} would have been skipped, {sb.sample.editedAnyway} of those edited</b></div>
            </div>
            <div className="vp-score-actions">
              <Link to="/admin/videos/review">{sb.toReview ? `Review ${sb.toReview} waiting →` : 'Nothing waiting for your review'}</Link>
              {cfg && (
                <label className="vp-sample" htmlFor="vp-sample-input">Claude on every video for a sample of
                  <input id="vp-sample-input" type="number" min="0" max="100" defaultValue={Math.round((cfg.claudeSampleAlways || 0) * 100)}
                    onBlur={(e) => { const p = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)); if (p / 100 !== cfg.claudeSampleAlways) saveConfig({ claudeSampleAlways: p / 100 }); }} />%
                </label>
              )}
            </div>
          </section>
        );
      })()}

      <div className="vp-filters" role="tablist" aria-label="Filter" ref={listTop}>
        {FILTERS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={filter === k} className={filter === k ? 'on' : ''} onClick={() => chooseFilter(k)}>{label} <span>{counts[k]}</span></button>
        ))}
        <label className="vp-hide">
          <input type="checkbox" checked={hideNoVideo} onChange={(e) => toggleHide(e.target.checked)} />
          <span>Hide activities without a video</span>
        </label>
        <button type="button" className="vp-refresh" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>

      {data && shown.length === 0 && <p className="vp-empty">{pageIndex === 0 ? 'No videos here yet. New activities are queued as their maps finish.' : 'Nothing older here.'}</p>}
      <div className="vp-list">
        {shown.map((item) => (item.state === 'skipped'
          ? <div key={item.id} className="vp-skipped"><b>{(item.activity || {}).name || item.id}</b> · {(item.activity || {}).km} km · {item.reason} · {when(item.createdAt)}</div>
          : <VideoCard key={item.id} item={item} />))}
      </div>
      {data && (pageIndex > 0 || hasOlder) && (
        <nav className="vp-pager" aria-label="Pages">
          <button type="button" onClick={() => goTo(pageIndex - 1)} disabled={pageIndex === 0 || loading}>← Newer</button>
          <span>Page {pageIndex + 1}</span>
          <button type="button" onClick={() => goTo(pageIndex + 1)} disabled={!hasOlder || loading}>Older →</button>
        </nav>
      )}
      {data && <p className="vp-footnote">Pipeline {data.pipelineVersion}. GPU cost is RunPod's serverless rate for the worker's billed time (facades included: our own GPU, no external AI API); Claude is Opus 5.5.</p>}
    </div>
  );
}

export default AdminVideosPage;
